<template>
  <div>

    <!-- ═══════════════════════ MOBILE ═══════════════════════ -->
    <div class="pb-5 overflow-hidden" v-if="isMobileOrTablet">
      <appHeader></appHeader>
      <transition appear name="fade">
        <div class="container cart pt-4 mt-2">

          <!-- Stepper -->
          <div class="cart-stepper mb-4">
            <div class="cs-step active">
              <div class="cs-dot">1</div>
              <span>Items</span>
            </div>
            <div class="cs-line"></div>
            <div class="cs-step">
              <div class="cs-dot">2</div>
              <span>Shipping</span>
            </div>
            <div class="cs-line"></div>
            <div class="cs-step">
              <div class="cs-dot">3</div>
              <span>Summary</span>
            </div>
          </div>

          <form v-on:submit.prevent="">
            <div class="row">
              <div class="col-12 mb-3">

                <!-- Not enough points -->
                <div v-if="hasEnoughPoints === false" class="cart-alert-danger mb-3">
                  <font-awesome-icon icon="info-circle" class="cart-alert-icon" />
                  <div>
                    <strong>Not enough points to complete this purchase.</strong>
                    <div>Please adjust your cart before placing the order.</div>
                  </div>
                </div>

                <!-- Cart items -->
                <div
                  v-for="item in cartItems"
                  :key="item.id"
                  class="cart-card"
                >
                  <router-link
                    :to="{ name: 'Product', params: { productId: item.productId } }"
                    class="cart-img-wrap"
                  >
                    <img :alt="item.productName" :src="item.imageUrl" />
                  </router-link>

                  <div class="cart-body">
                    <router-link
                      :to="{ name: 'Product', params: { productId: item.productId } }"
                      class="cart-name"
                    >
                      {{ item.productName }}
                      <span v-if="item.productVersionSpecValue" class="cart-variant">
                        · {{ item.productVersionSpecValue }}
                      </span>
                    </router-link>

                    <div v-if="!item.productVersionDeleted" class="cart-price">
                      <span class="cart-price-num">
                        {{ itemPointsDisplayed(item.pointsPerUnit, item.taxPointsPerUnit) | numToLocale }}
                      </span>
                      <span class="cart-price-label">Points</span>
                      <span class="cart-price-tax">+Sales Tax</span>
                    </div>
                    <div v-if="!item.productVersionDeleted" class="cart-instock">
                      <span class="cart-instock-dot"></span> In stock
                    </div>
                    <div v-else class="cart-unavail">
                      <font-awesome-icon icon="exclamation-circle" />
                      Unavailable · <strong>Remove to continue</strong>
                    </div>

                    <!-- Mobile qty controls -->
                    <div class="cart-actions">
                      <div class="qty-wrap" v-if="!item.productVersionDeleted">
                        <button class="qty-btn" v-on:click="onCartItemQuantityChangeMobile(item.id, '-')">−</button>
                        <span class="qty-val">{{ item.quantity }}</span>
                        <button class="qty-btn" v-on:click="onCartItemQuantityChangeMobile(item.id, '+')">+</button>
                      </div>
                      <button class="btn-remove" v-on:click="deleteCartItem(item.id)">
                        <font-awesome-icon icon="trash" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Mobile summary -->
              <div class="col-12">
                <div class="mobile-summary-card">
                    <div class="msc-title">Order summary</div>

                    <div class="msc-row">
                        <span class="msc-row-label">Points balance</span>
                        <span class="msc-row-val blue">{{ pointsAvailable | numToLocale }}</span>
                    </div>
                    <div class="msc-row">
                        <span class="msc-row-label">Products</span>
                        <span class="msc-row-val">{{ pointsProductDisplayed | numToLocale }} pts</span>
                    </div>

                    <div v-if="supplierSalesTax > 0" class="msc-row">
                        <span class="msc-row-label">Sales tax</span>
                        <span class="msc-row-val">{{ supplierSalesTax | numToLocale }} pts</span>
                    </div>
                    <div v-else class="cart-tax-note mt-1 mb-1">
                        <font-awesome-icon icon="info-circle" />
                        Sales tax calculated at checkout based on your zip code
                    </div>

                    <transition name="slide-fade">
                        <div
                        v-if="allowTaxPaymentWithPoints && !grossUpTaxes && payTaxes"
                        class="msc-row"
                        >
                        <span class="msc-row-label">Income taxes</span>
                        <span class="msc-row-val purple">{{ pointsTaxes | numToLocale }} pts</span>
                        </div>
                    </transition>

                    <div class="msc-total-row">
                        <span class="msc-total-label">Total</span>
                        <span class="msc-total-val">{{ totalPoints | numToLocale }} pts</span>
                    </div>

                    <div class="msc-row">
                        <span class="msc-row-label">Remaining balance</span>
                        <span class="msc-row-val green">{{ remainingPoints | numToLocale }} pts</span>
                    </div>

                    <div v-if="allowTaxPaymentWithPoints && !grossUpTaxes" class="msc-checkbox-row">
                        <input
                        id="points-for-taxes-m"
                        :checked="payTaxes"
                        v-on:click="setUsePointsToPayTaxes(!payTaxes)"
                        type="checkbox"
                        class="mr-2"
                        />
                        <label for="points-for-taxes-m">Use my points to pay for income tax</label>
                    </div>

                    <button class="btn-continue mt-2" v-on:click="validateCartAndContinue()">
                        Continue to shipping
                    </button>
                    <p class="msc-note">
                        Points are subject to change based on availability and vendor fulfillment.
                    </p>
                    <!-- Notice banner — mobile -->
                    <div class="cart-notice-banner mb-3">
                      <div class="cart-notice-summary">
                        <font-awesome-icon icon="info-circle" class="mr-1" />
                        Important order information
                      </div>
                      <ul class="cart-notice-list">
                        <li><strong>All sales are final</strong> – purchases, including gift cards and physical items, cannot be returned or refunded.</li>
                        <li>All gift cards are <strong>digital</strong> and will be delivered electronically.</li>
                        <li>Gift cards must be activated within <strong>120 days</strong> of issuance or all rights will lapse.</li>
                        <li>Claim requests must be submitted <strong>before</strong> the 30-day post order window closes.</li>
                        <li v-if="companyName == 'Pentair'"><strong>Note:</strong> $1 earned in the myHC360+ app = 1 Rewards Mall point.</li>
                      </ul>
                    </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </transition>
      <appFooter></appFooter>
      <navigationFooter></navigationFooter>
    </div>

    <!-- ═══════════════════════ DESKTOP ═══════════════════════ -->
    <div v-else class="mt-3">
      <appHeader></appHeader>
      <transition appear name="fade">
        <div class="container cart px-4 pt-3 m-auto">

          <!-- Stepper -->
          <div class="cart-stepper mb-4">
            <div class="cs-step active">
              <div class="cs-dot">1</div>
              <span>View items</span>
            </div>
            <div class="cs-line"></div>
            <div class="cs-step">
              <div class="cs-dot">2</div>
              <span>Shipping information</span>
            </div>
            <div class="cs-line"></div>
            <div class="cs-step">
              <div class="cs-dot">3</div>
              <span>Order summary</span>
            </div>
          </div>

          <!-- Collapsible notice banner -->
          <!-- Notice banner — mobile -->
          <div class="cart-notice-banner mb-3">
            <div class="cart-notice-summary">
              <font-awesome-icon icon="info-circle" class="mr-1" />
              Important order information
            </div>
            <ul class="cart-notice-list">
              <li><strong>All sales are final</strong> – purchases, including gift cards and physical items, cannot be returned or refunded.</li>
              <li>All gift cards are <strong>digital</strong> and will be delivered electronically.</li>
              <li>Gift cards must be activated within <strong>120 days</strong> of issuance or all rights will lapse.</li>
              <li>Claim requests must be submitted <strong>before</strong> the 30-day post order window closes.</li>
              <li v-if="companyName == 'Pentair'"><strong>Note:</strong> $1 earned in the myHC360+ app = 1 Rewards Mall point.</li>
            </ul>
          </div>

          <form v-on:submit.prevent="">
            <div class="row">
              <!-- Left: items -->
              <div class="col-lg-9 mb-4 pr-4">

                <div class="section-header mb-3">
                  <h5 class="section-title">
                    Shopping cart
                    <span class="section-count">({{ itemsCount | numToLocale }})</span>
                  </h5>
                </div>

                <div v-if="hasEnoughPoints === false" class="cart-alert-danger mb-3">
                  <font-awesome-icon icon="info-circle" class="cart-alert-icon" />
                  <div>
                    <strong>Not enough points to complete this purchase.</strong>
                    <div>Please adjust your cart before placing the order.</div>
                  </div>
                </div>

                <div
                  v-for="item in cartItems"
                  :key="item.id"
                  class="cart-card"
                >
                  <router-link
                    :to="{ name: 'Product', params: { productId: item.productId } }"
                    class="cart-img-wrap"
                  >
                    <img :alt="item.productName" :src="item.imageUrl" />
                  </router-link>

                  <div class="cart-body">
                    <router-link
                      :to="{ name: 'Product', params: { productId: item.productId } }"
                      class="cart-name"
                    >
                      {{ item.productName }}
                      <span v-if="item.productVersionSpecValue" class="cart-variant">
                        · {{ item.productVersionSpecValue }}
                      </span>
                    </router-link>

                    <div v-if="!item.productVersionDeleted" class="cart-price">
                      <span class="cart-price-num">
                        {{ itemPointsDisplayed(item.pointsPerUnit, item.taxPointsPerUnit) | numToLocale }}
                      </span>
                      <span class="cart-price-label">Points</span>
                      <span class="cart-price-tax">+Sales Tax</span>
                    </div>
                    <div v-if="!item.productVersionDeleted" class="cart-instock">
                      <span class="cart-instock-dot"></span> In stock
                    </div>
                    <div v-else class="cart-unavail">
                      <font-awesome-icon icon="exclamation-circle" />
                      Currently unavailable · <strong>Remove to continue</strong>
                    </div>

                    <div class="cart-actions">
                      <div class="cart-qty-group" v-if="!item.productVersionDeleted">
                        <label class="qty-label">Qty</label>
                        <b-form-input
                          id="quantity"
                          class="cart-qty-input"
                          v-model.number="item.quantity"
                          min="1"
                          type="number"
                          @change="onCartItemQuantityChange(item.id)"
                        ></b-form-input>
                      </div>
                      <span class="cart-action-divider" v-if="!item.productVersionDeleted"></span>
                      <button class="btn-remove" v-on:click="deleteCartItem(item.id)">
                        <font-awesome-icon icon="trash" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Right: summary -->
              <div class="col-lg-3">
                <div class="summary-card">
                  <div class="summary-card-header">
                    <span class="summary-card-title">Order summary</span>
                  </div>

                  <div class="summary-rows">
                    <div class="summary-row">
                      <span class="sr-label">Points balance</span>
                      <span class="sr-val blue">{{ pointsAvailable | numToLocale }}</span>
                    </div>
                    <div class="summary-divider"></div>
                    <div class="summary-row">
                      <span class="sr-label">Products</span>
                      <span class="sr-val">{{ pointsProductDisplayed | numToLocale }} pts</span>
                    </div>
                    <div class="summary-divider"></div>
                    <div class="summary-row" v-if="supplierSalesTax > 0">
                      <span class="sr-label">Sales tax</span>
                      <span class="sr-val">{{ supplierSalesTax | numToLocale }} pts</span>
                    </div>
                    <div v-else class="cart-tax-note mx-2 mb-2 mt-1">
                      <font-awesome-icon icon="info-circle" />
                      Sales tax calculated at checkout based on your zip code
                    </div>
                    <transition name="slide-fade">
                      <div
                        v-if="allowTaxPaymentWithPoints && !grossUpTaxes && payTaxes"
                        class="summary-row"
                      >
                        <span class="sr-label">Income taxes</span>
                        <span class="sr-val purple">{{ pointsTaxes | numToLocale }} pts</span>
                      </div>
                    </transition>
                  </div>

                  <div class="summary-total-row">
                    <span class="summary-total-label">Total</span>
                    <span class="summary-total-val">{{ totalPoints | numToLocale }} pts</span>
                  </div>
                  <div class="summary-row">
                    <span class="sr-label">Remaining balance</span>
                    <span class="sr-val green">{{ remainingPoints | numToLocale }} pts</span>
                  </div>

                  <div v-if="allowTaxPaymentWithPoints && !grossUpTaxes" class="summary-checkbox-row">
                    <input
                      id="points-for-taxes"
                      :checked="payTaxes"
                      v-on:click="setUsePointsToPayTaxes(!payTaxes)"
                      class="custom-control-input mr-2"
                      type="checkbox"
                    />
                    <label for="points-for-taxes">Use my points to pay for income tax</label>
                  </div>

                  <div class="summary-action">
                    <button
                      class="btn-continue"
                      v-on:click="validateCartAndContinue()"
                    >
                      Continue to shipping
                    </button>
                  </div>

                  <div class="summary-note">
                    Points are subject to change based on availability and vendor fulfillment. The latest point value will be verified when displayed in cart.
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </transition>
      <appFooter></appFooter>
    </div>

  </div>
</template>
<script>
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { mapState } from "vuex";
import navigationFooter from "../../components/navigationFooter.vue";
import { isMobile, isTablet } from "mobile-device-detect";
import FrontendService from "../../services/FrontendService";

export default {
  components: {
    appHeader: Header,
    appFooter: Footer,
    navigationFooter: navigationFooter,
  },
  data() {
    return {
      checkoutProgressMax: 100,
      checkoutProgressCurrent: 25,
      supplierSalesTax: 0,
    };
  },
  computed: {
    ...mapState({
      // TODO get "status.X.initialized" to handle the "loading..."
      pointsUser: (state) => state.user.profile.pointsAvailable,
      pointsUnapproved: (state) => state.cart.unapproved.productPoints,

      cartItems: (state) => state.cart.items,
      cartResponse: (state) => state.cart,

      itemsCount: (state) => state.cart.summary.items,
      pointsProduct: (state) => state.cart.summary.productPoints,
      pointsTaxes: (state) => state.cart.summary.taxPoints,
      allowTaxPaymentWithPoints: (state) =>
        state.company.allowTaxPaymentWithPoints,
      grossUpTaxes: (state) => state.company.grossUpTaxes,
      payTaxes: (state) => state.cart.usePointsToPayTaxes,
      companyName: (state) =>
        state.company.name,
    }),
    pointsAvailable: function() {
      return this.pointsUser - this.pointsUnapproved;
    },
    pointsProductDisplayed: function() {
      if (this.grossUpTaxes) {
        return this.pointsProduct + this.pointsTaxes;
      } else {
        return this.pointsProduct;
      }
    },
    totalPoints: function() {
      if (this.grossUpTaxes) {
        return this.pointsProduct + this.pointsTaxes + this.supplierSalesTax;
      } else if (this.allowTaxPaymentWithPoints && this.payTaxes) {
        return this.pointsProduct + this.pointsTaxes  + this.supplierSalesTax;
      } else {
        return this.pointsProduct + this.supplierSalesTax;
      }
    },
    remainingPoints: function() {
      return this.pointsAvailable - this.totalPoints;
    },
    hasEnoughPoints: function() {
      return this.remainingPoints >= 0;
    },
    isMobileOrTablet: function() {
      return isMobile || isTablet;
    },
  },
  beforeMount(){
    this.getCartDetails();
    this.scrollToTop();
  },
  methods: {
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    itemPointsDisplayed: function(pointsPerUnit, taxPointsPerUnit) {
      if (this.grossUpTaxes) {
        return pointsPerUnit + taxPointsPerUnit;
      } else {
        return pointsPerUnit;
      }
    },
    onCartItemQuantityChangeMobile: function(cartItemId, value) {
      const item = this.cartItems.find((it) => it.id === cartItemId);
      if (value === "-") {
        item.quantity = item.quantity - 1;
      } else if (value === "+") {
        item.quantity = item.quantity + 1;
      }
      if (item.quantity > 0) {
        this.updateCartItem(cartItemId);
      } else {
        this.$store.dispatch("cart/fetchCart");
      }
    },
    onCartItemQuantityChange: function (cartItemId) {
      const item = this.cartItems.find(it => it.id === cartItemId)
      if (item.quantity > 0) {
        this.updateCartItem(cartItemId)
      } else {
        this.$store.dispatch('cart/fetchCart')
      }
    },
    updateCartItem: function(cartItemId) {
      const item = this.cartItems.find((it) => it.id === cartItemId);
      this.$store.dispatch("cart/patchCartItem", {
        cartItemId: item.id,
        productVersionId: item.productVersionId,
        quantity: item.quantity,
      }).then((response) => {
        this.getCartDetails();
      });
    },
    deleteCartItem: function(cartItemId) {
      this.$store.dispatch("cart/removeCartItem", cartItemId)
      .then((response) => {
        this.getCartDetails();
      })
      .catch(() => {
        this.showError("Failed to remove product from cart");
      });

    },
    setUsePointsToPayTaxes: function(payTaxes) {
      this.$store.dispatch("cart/setUsePointsToPayTaxes", payTaxes);
    },
    validateCartAndContinue: function() {
      if (this.grossUpTaxes === true) {
        this.$store.dispatch("cart/setUsePointsToPayTaxes", true);
      }
      this.$store
        .dispatch("cart/validateCart", this.payTaxes)
        .then((response) => {
          if (response.isCartValid) {
            this.$router.push({ name: "Checkout" });
          } else {
            this.showError(response.errorsArray.join("\n"));
          }
        })
        .catch(() => {
          this.showError("Invalid cart");
        });
    },
    showSuccess: function(message, onClose) {
      this.$bvModal
        .msgBoxOk(message, {
          size: "sm",
          headerClass: "p-2 border-bottom-0",
          footerClass: "p-2 border-top-0",
          centered: true,
        })
        .then(() => {
          onClose();
        });
    },
    showError: function(message) {
      this.$bvModal.msgBoxOk(message, {
        size: "sm",
        headerClass: "p-2 border-bottom-0",
        footerClass: "p-2 border-top-0",
        okVariant: "danger",
        centered: true,
      });
    },
    getCartDetails() {
      let data = { offset: 0, limit: 20,}
      FrontendService.getCart(data)
        .then((response) => {
          this.supplierSalesTax = response.data.total_sales_tax;
        }).catch((e) => {
          console.log(e.response);
        });
    }
  },
  created() {
    this.$store.dispatch("cart/fetchCart");
    this.$store.dispatch("cart/fetchCartSummary");
    this.$store.dispatch("cart/fetchCartUnapproved");
    this.$store.dispatch("user/fetchProfile");
  },
};
</script>
<style lang="scss" scoped>
.container.cart {
  min-height: 90vh;
}

/* ── Stepper ── */
.cart-stepper {
  display: flex;
  align-items: center;
}
.cs-step {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: $gray-500;
}
.cs-step.active {
  color: $primary;
  font-weight: 500;
}
.cs-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid $gray-300;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  background: #fff;
  color: $gray-500;
  flex-shrink: 0;
}
.cs-step.active .cs-dot {
  border-color: $primary;
  color: $primary;
  background: lighten($primary, 44%);
}
.cs-line {
  flex: 1;
  height: 1px;
  background: $gray-200;
  min-width: 20px;
}

/* ── Notice banner ── */
.cart-notice-banner {
  background: $gray-100;
  border: 1px solid $gray-200;
  border-radius: $border-radius;
  padding: .75rem 1rem;
  font-size: 12.5px;
  color: $gray-700;
  cursor: pointer;
}
.cart-notice-summary {
  font-size: 13px;
  font-weight: 500;
  color: $gray-700;
  list-style: none;
  &::-webkit-details-marker { display: none; }
}
.cart-notice-list {
  margin-top: 8px;
  padding-left: 16px;
  li { padding: 3px 0; line-height: 1.55; }
}

/* ── Alert ── */
.cart-alert-danger {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: lighten($danger, 42%);
  border: 1px solid lighten($danger, 30%);
  border-radius: $border-radius;
  padding: .85rem 1rem;
  font-size: 13px;
  color: darken($danger, 10%);
}
.cart-alert-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

/* ── Section header ── */
.section-header { display: flex; align-items: center; }
.section-title {
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
}
.section-count {
  font-size: 0.875rem;
  color: $gray-500;
  margin-left: 6px;
  font-weight: 400;
}

/* ── Cart card ── */
.cart-card {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: $border-radius;
  padding: 1rem;
  margin-bottom: .75rem;
  transition: border-color .15s ease;
  &:hover { border-color: $gray-400; }
}

.cart-img-wrap {
  display: block;
  width: 90px;
  min-width: 90px;
  height: 90px;
  border-radius: $border-radius-sm;
  overflow: hidden;
  background: $gray-100;
  border: 1px solid $gray-200;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    padding: 4px;
  }
}

.cart-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.cart-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: $gray-900;
  text-decoration: none;
  line-height: 1.4;
  &:hover { color: $primary; text-decoration: none; }
}
.cart-variant {
  font-size: 0.8rem;
  font-weight: 400;
  color: $gray-500;
}

/* Points display — matches product detail page */
.cart-price {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin: 2px 0;
}
.cart-price-num {
  font-size: 1.25rem;
  font-weight: 700;
  color: $primary;
  line-height: 1;
}
.cart-price-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: $primary;
  text-transform: uppercase;
  letter-spacing: .05em;
}
.cart-price-tax {
  font-size: 0.7rem;
  font-weight: 600;
  color: #c3340a;
  margin-left: 2px;
}

.cart-instock {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  color: $success;
}
.cart-instock-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $success;
  flex-shrink: 0;
}

.cart-unavail {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: $danger;
  background: lighten($danger, 42%);
  border-radius: $border-radius-sm;
  padding: 4px 10px;
  width: fit-content;
}

/* ── Cart actions ── */
.cart-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.cart-qty-group {
  display: flex;
  align-items: center;
  gap: 6px;
}
.qty-label {
  font-size: 0.8rem;
  color: $gray-500;
  margin: 0;
}
.cart-qty-input {
  width: 64px !important;
  height: 30px !important;
  font-size: 0.85rem !important;
  padding: 0 8px !important;
  display: inline-block;
}
.cart-action-divider {
  width: 1px;
  height: 18px;
  background: $gray-300;
  display: inline-block;
}

/* Mobile qty stepper */
.qty-wrap {
  display: flex;
  align-items: center;
  border: 1px solid $gray-300;
  border-radius: $border-radius-sm;
  overflow: hidden;
}
.qty-btn {
  width: 28px;
  height: 28px;
  background: $gray-100;
  border: none;
  font-size: 1rem;
  font-weight: 500;
  color: $gray-800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: $gray-200; }
}
.qty-val {
  width: 34px;
  height: 28px;
  background: #fff;
  border-left: 1px solid $gray-300;
  border-right: 1px solid $gray-300;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-remove {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: $danger;                          // ← was $gray-500, now always red
  padding: 5px 10px;
  border-radius: $border-radius-sm;
  cursor: pointer;
  border: none;
  background: transparent;
  &:hover { color: darken($danger, 10%); background: lighten($danger, 42%); }
}

/* ── Tax note ── */
.cart-tax-note {
  font-size: 0.75rem;
  color: #c3340a;
  background: #fff3e0;
  border-radius: $border-radius-sm;
  padding: 5px 10px;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: .5rem;
}

/* ── Summary card (desktop) ── */
.summary-card {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: $border-radius;
  overflow: hidden;
  position: sticky;
  top: 80px;
}
.summary-card-header {
  padding: .85rem 1rem;
  border-bottom: 1px solid $gray-200;
}
.summary-card-title {
  font-size: 0.95rem;
  font-weight: 500;
}
.summary-rows { padding: .25rem 0; }
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .55rem 1rem;
  font-size: 0.825rem;
}
.sr-label { color: $gray-700; }
.sr-val {
  font-weight: 500;
  color: $gray-800;
  &.blue  { color: $primary; }
  &.green { color: $success; }
  &.red   { color: $danger; }
  &.purple{ color: #9b51e0; }
}
.summary-divider {
  height: 1px;
  background: $gray-100;
  margin: 0 1rem;
}
.summary-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .75rem 1rem;
  background: $gray-100;
  border-top: 1px solid $gray-200;
  border-bottom: 1px solid $gray-200;
}
.summary-total-label { font-size: 0.9rem; font-weight: 500; }
.summary-total-val   { font-size: 1rem; font-weight: 700; color: $danger; }
.summary-checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: .75rem 1rem;
  font-size: 0.8rem;
  color: $gray-600;
  border-top: 1px solid $gray-200;
  background: $gray-100;  // ← fixed
  label { cursor: pointer; line-height: 1.4; margin: 0; }
}
.summary-action { padding: .75rem 1rem; }
.btn-continue {
  display: block;
  width: 100%;
  background: $primary;
  border: none;
  border-radius: $border-radius-sm;
  padding: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  text-align: center;
  transition: background .15s ease;
  &:hover { background: darken($primary, 8%); }
}
.summary-note {
  padding: .75rem 1rem;
  border-top: 1px solid $gray-200;
  font-size: 0.72rem;
  color: $gray-700;
  line-height: 1.5;
}

/* ── Mobile summary card ── */
.mobile-summary-card {
  background: #fff;
  border: 1px solid $gray-200;
  border-radius: $border-radius;
  padding: 1rem;
  margin-top: .5rem;
}
.msc-title {
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: .5rem;
  padding-bottom: .5rem;
  border-bottom: 1px solid $gray-200;
}
.msc-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.825rem;
  padding: .55rem 0;
  border-bottom: 1px solid $gray-100;
}
.msc-row-label { color: $gray-700; }
.msc-row-val {
  font-weight: 500;
  color: $gray-800;
  &.blue   { color: $primary; }
  &.green  { color: $success; }
  &.red    { color: $danger; }
  &.purple { color: #9b51e0; }
}
.msc-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: .65rem 0;
  background: $gray-100;
  border-top: 1px solid $gray-200;
  border-bottom: 1px solid $gray-200;
  margin: 0 -1rem;
  padding: .65rem 1rem;
}
.msc-total-label { font-size: 0.9rem; font-weight: 500; }
.msc-total-val   { font-size: 1rem; font-weight: 700; color: $danger; }
.msc-checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.8rem;
  color: $gray-600;
  padding: .6rem 0;
  border-bottom: 1px solid $gray-100;
  label { cursor: pointer; line-height: 1.4; margin: 0; }
}
.msc-note {
  font-size: 0.8rem;
  color: $gray-700;
  line-height: 1.5;
  margin-top: .5rem;
}

// remove old grid styles — no longer needed
// .msc-grid, .msc-cell, .msc-label, .msc-val — delete these

/* ── Mobile image ── */
@media (max-width: 576px) {
  .cart-img-wrap {
    width: 75px;
    min-width: 75px;
    height: 75px;
  }
  .cart-card {
    padding: .85rem;
    gap: .75rem;
  }
}
</style>

<style lang="scss">
.slide-fade-enter-active { transition: all .3s ease; }
.slide-fade-leave-active { transition: all .3s ease; }
.slide-fade-enter, .slide-fade-leave-to { transform: translateX(10px); opacity: 0; }
.sales-tax-text {
  text-transform: capitalize;
  font-weight: 600 !important;
  color: #c3340a;
  font-size: .7em !important;
}
</style>